import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { motion } from "framer-motion";
import {
  Bell, ClockAlert, Coffee, Download, KeyRound, LogOut, RefreshCw, Search, Store, Users, UserX,
} from "lucide-react";
import { api, fmtFecha } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import VisorArchivo from "@/components/VisorArchivo";
import { fechaLargaISO, hoyISOBA } from "@/components/ClockBA";
import { Avatar, Badge, Card, FechaNav, Kpi, PageHeader, TONOS, btnSec, inputCls } from "@/components/rrhh/ui";

const ALERTA_UI = {
  sin_salida: { titulo: "Falta la salida", icon: LogOut, tone: "rojo", orden: 0 },
  ausencia: { titulo: "No registró la entrada", icon: UserX, tone: "rojo", orden: 1 },
  pin_bloqueado: { titulo: "PIN bloqueado", icon: KeyRound, tone: "rojo", orden: 2 },
  tardanza: { titulo: "Llegó tarde", icon: ClockAlert, tone: "amarillo", orden: 3 },
  retiro_anticipado: { titulo: "Se retiró antes", icon: LogOut, tone: "amarillo", orden: 4 },
  justificativo_pendiente: { titulo: "Justificativo para revisar", icon: Bell, tone: "azul", orden: 5 },
};

export default function HoyPage() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { user } = useAuth();
  const esLiq = user?.rol === "liquidacion";
  const rutaPersona = (id, fechaIso) => (esLiq
    ? `/liquidacion/persona/${id}?mes=${fechaIso.slice(0, 7)}`
    : `/rrhh/empleados/${id}?fecha=${fechaIso}`);
  const [fecha, setFecha] = useState(hoyISOBA());
  const [localId, setLocalId] = useState("");
  const [busqueda, setBusqueda] = useState("");
  const [fotoVer, setFotoVer] = useState(null);

  const { data: locales } = useQuery({
    queryKey: ["locales"],
    queryFn: async () => (await api.get("/locales")).data,
  });
  const { data, refetch } = useQuery({
    queryKey: ["hoy", fecha, localId],
    queryFn: async () => (await api.get("/rrhh/hoy", {
      params: { fecha, local_id: localId || undefined },
    })).data,
    refetchInterval: 5000,
  });
  const { data: alertas } = useQuery({
    queryKey: ["alertas", "abierta"],
    queryFn: async () => (await api.get("/rrhh/alertas", { params: { estado: "abierta" } })).data,
    refetchInterval: 5000,
  });

  const r = data?.resumen;
  const esHoy = fecha === hoyISOBA();

  const grupos = useMemo(() => {
    const filas = (data?.filas || []).filter((f) => {
      if (!busqueda.trim()) return true;
      const q = busqueda.trim().toLowerCase();
      return f.nombre.toLowerCase().includes(q) || f.legajo.includes(q);
    });
    const porLocal = new Map();
    for (const f of filas) {
      if (!porLocal.has(f.local_id)) porLocal.set(f.local_id, { nombre: f.local, filas: [] });
      porLocal.get(f.local_id).filas.push(f);
    }
    const orden = (locales || []).map((l) => l.id);
    return [...porLocal.entries()].sort((a, b) => orden.indexOf(a[0]) - orden.indexOf(b[0]));
  }, [data, busqueda, locales]);

  const alertasOrdenadas = useMemo(
    () => [...(alertas || [])].sort((a, b) => (ALERTA_UI[a.tipo]?.orden ?? 9) - (ALERTA_UI[b.tipo]?.orden ?? 9)),
    [alertas],
  );

  const actualizar = () => {
    refetch();
    qc.invalidateQueries({ queryKey: ["alertas"] });
    qc.invalidateQueries({ queryKey: ["alertas-resumen"] });
  };

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25 }} data-testid="pagina-hoy">
      <PageHeader
        titulo="Resumen del día"
        subtitulo={`${fechaLargaISO(fecha)}: cómo viene la jornada en cada local.`}
        testid="header-hoy"
      >
        <button className={`${btnSec} flex items-center gap-2`} onClick={actualizar} data-testid="btn-actualizar">
          <RefreshCw size={14} /> Actualizar
        </button>
        {!esLiq && (
          <button
            className={`${btnSec} flex items-center gap-2`}
            onClick={() => navigate("/rrhh/exportar")}
            data-testid="btn-exportar-mes"
          >
            <Download size={14} /> Exportar el mes
          </button>
        )}
      </PageHeader>

      <div className="mb-5">
        <FechaNav fecha={fecha} onChange={setFecha} testid="nav-fecha-hoy" />
      </div>

      <div className="mb-5 grid grid-cols-2 gap-3.5 xl:grid-cols-4">
        <Kpi icon={Users} tone="verde" label="Presentes" value={`${r?.presentes ?? "—"} / ${r?.esperados ?? "—"}`}
             sub={esHoy ? "Con la entrada fichada hoy" : "Con la entrada fichada ese día"} testid="card-presentes" />
        <Kpi icon={Coffee} tone="amarillo" label="En pausa" value={r?.en_pausa ?? "—"}
             sub="Ahora, entre los presentes" testid="card-en-pausa" />
        <Kpi icon={UserX} tone="rojo" label="Sin fichar" value={r?.sin_fichar ?? "—"}
             sub="Tenían que venir y no hay entrada" testid="card-sin-fichar" />
        <Kpi icon={Bell} tone="rojo" label="Alertas" value={r?.alertas_abiertas ?? "—"}
             sub="Para revisar" testid="card-alertas" />
      </div>

      <div className="grid items-start gap-5 xl:grid-cols-[1fr_360px]">
        <Card
          testid="card-equipo"
          titulo="Equipo por local"
          subtitulo={`${fmtFecha(fecha)} · agrupado por local base`}
          right={
            <span className="flex items-center gap-1.5 text-xs text-[#2DD4BF]" data-testid="datos-hora">
              <span className="h-1.5 w-1.5 rounded-full bg-[#2DD4BF]" /> Datos de las {data?.datos_hora ?? "--:--"}
            </span>
          }
          bodyCls="p-0"
        >
          <div className="flex flex-wrap items-center gap-2 border-b border-white/8 px-5 py-3.5">
            <select value={localId} onChange={(e) => setLocalId(e.target.value)} className={inputCls} data-testid="filtro-local-hoy">
              <option value="">Todos los locales</option>
              {locales?.map((l) => <option key={l.id} value={l.id}>{l.nombre}</option>)}
            </select>
            <div className="relative">
              <Search size={14} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-600" />
              <input
                value={busqueda}
                onChange={(e) => setBusqueda(e.target.value)}
                placeholder="Buscar por nombre o legajo"
                className={`${inputCls} pl-8`}
                data-testid="buscar-persona"
              />
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-white/8 text-left text-[11px] uppercase tracking-wider text-gray-500">
                  <th className="px-5 py-3 font-semibold">Persona</th>
                  <th className="px-4 py-3 font-semibold">Estado</th>
                  <th className="px-4 py-3 font-semibold">Entrada</th>
                  <th className="px-4 py-3 font-semibold">Pausas</th>
                  <th className="px-4 py-3 font-semibold">Trabajado</th>
                  <th className="px-5 py-3 font-semibold">Tardanzas del mes</th>
                </tr>
              </thead>
              <tbody>
                {grupos.map(([localIdGrupo, g]) => (
                  <GrupoLocal key={localIdGrupo} nombre={g.nombre} filas={g.filas} onFicha={(id) => navigate(rutaPersona(id, fecha))} />
                ))}
                {grupos.length === 0 && (
                  <tr><td colSpan={6} className="px-5 py-10 text-center text-sm text-gray-500">Nadie coincide con la búsqueda</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </Card>

        <Card testid="card-alertas-hoy" titulo={`Alertas · ${alertasOrdenadas.length}`} subtitulo="De la más urgente a la menos" bodyCls="p-3.5">
          <div className="space-y-2.5">
            {alertasOrdenadas.length === 0 && (
              <p className="px-2 py-8 text-center text-sm text-gray-500">No hay alertas abiertas</p>
            )}
            {alertasOrdenadas.map((a) => {
              const ui = ALERTA_UI[a.tipo] || { titulo: a.tipo, icon: Bell, tone: "gris" };
              const Icon = ui.icon;
              return (
                <div key={a.id} className="flex items-center gap-3 rounded-xl border border-white/8 bg-[#0B0F14] p-3" data-testid={`alerta-hoy-${a.id.slice(0, 8)}`}>
                  <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl ${TONOS[ui.tone]}`}>
                    <Icon size={16} />
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="text-[13px] font-semibold text-white">{ui.titulo}</p>
                    <button
                      onClick={() => navigate(rutaPersona(a.empleado_id, a.fecha_hora.slice(0, 10)))}
                      className="block truncate text-left text-xs font-medium text-[#2DD4BF] hover:underline"
                      data-testid={`alerta-empleado-${a.id.slice(0, 8)}`}
                    >
                      {a.empleado_nombre}
                    </button>
                    <p className="truncate text-[11px] text-gray-500">{fmtFecha(a.fecha_hora)} · {a.detalle}</p>
                  </div>
                  {!esLiq && (
                    <button
                      onClick={() => navigate("/rrhh/alertas")}
                      className="shrink-0 text-xs font-semibold text-gray-400 transition-colors hover:text-white"
                      data-testid={`alerta-ver-${a.id.slice(0, 8)}`}
                    >
                      Ver
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </Card>
      </div>

      {fotoVer && <VisorArchivo url={fotoVer} onClose={() => setFotoVer(null)} titulo="Foto de entrada" />}
    </motion.div>
  );
}

function GrupoLocal({ nombre, filas, onFicha }) {
  return (
    <>
      <tr className="border-b border-white/8 bg-white/[0.03]">
        <td colSpan={6} className="px-5 py-2.5">
          <span className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.12em] text-gray-400">
            <Store size={13} className="text-[#2DD4BF]" />
            {nombre} · {filas.length} {filas.length === 1 ? "persona" : "personas"}
          </span>
        </td>
      </tr>
      {filas.map((f) => (
        <tr
          key={f.empleado_id}
          onClick={() => onFicha(f.empleado_id)}
          className="cursor-pointer border-b border-white/5 transition-colors last:border-0 hover:bg-[#2DD4BF]/[0.04]"
          data-testid={`hoy-fila-${f.legajo}`}
        >
          <td className="min-w-44 px-5 py-3">
            <span className="flex items-center gap-3">
              <Avatar iniciales={f.iniciales} size="sm" />
              <span className="whitespace-nowrap leading-tight">
                <span className="block text-sm font-semibold text-white">{f.nombre}</span>
                <span className="block font-mono-num text-[11px] text-gray-500">Legajo {f.legajo}</span>
              </span>
            </span>
          </td>
          <td className="px-4 py-3">
            <Badge color={f.estado_color} testid={`estado-${f.legajo}`}>{f.estado_label}</Badge>
          </td>
          <td className="px-4 py-3">
            {f.entrada_hora
              ? <span className="font-mono-num font-semibold text-[#2DD4BF]">{f.entrada_hora}</span>
              : <span className="font-mono-num text-gray-600">--:--</span>}
          </td>
          <td className="px-4 py-3">
            <span className="block font-mono-num text-xs text-gray-300">{f.pausas_usadas} / {f.pausas_tope} min</span>
            <span className="mt-1 block h-1 w-20 overflow-hidden rounded-full bg-white/10">
              <span
                className={`block h-full rounded-full ${f.pausas_usadas > f.pausas_tope ? "bg-red-400" : "bg-[#2DD4BF]"}`}
                style={{ width: `${Math.min(100, (f.pausas_usadas / f.pausas_tope) * 100)}%` }}
              />
            </span>
          </td>
          <td className="whitespace-nowrap px-4 py-3">
            {f.trabajado
              ? <span className="font-mono-num text-white">{f.trabajado}</span>
              : <span className="font-mono-num text-gray-600">--</span>}
          </td>
          <td className="whitespace-nowrap px-5 py-3">
            {f.tardanzas_mes > 0
              ? <span className="font-mono-num text-amber-300">{f.tardanzas_mes}</span>
              : <span className="text-gray-500">Ninguna</span>}
          </td>
        </tr>
      ))}
    </>
  );
}
